// The single source of truth for raisedbeef.com navigation.
//
// Imported by BOTH the desktop nav (RbSiteChrome) and the mobile hamburger
// (RbMobileMenu). Kept in its own module so neither imports the other — a
// cycle would be the easy way to reintroduce exactly the drift this file
// exists to prevent.
//
// Background: the desktop nav used to be hand-copied into all 7 public pages
// while the mobile menu had its own list. Wiring a link meant editing 7 files,
// and every miss left a dead `href="#"`. 34 accumulated, including "Sign in"
// on every page pointing at nothing while /auth existed the whole time.

import { rbBoxedBeefPath, rbCofPath, rbFedCattlePath } from "@/lib/siteHost";

export type RbNavKey =
  | "markets" | "dvauction" | "usda-reports" | "sales" | "boxed-beef" | "cof"
  | "fed-cattle" | "consultant" | "platform" | "reports" | "leaderboard";

export interface RbNavLink {
  key: RbNavKey;
  label: string;
  /** Resolved at render — boxed-beef/COF/fed-cattle paths are host-dependent. */
  path: () => string;
  /** Label used in the mobile panel when it should differ from the nav. */
  mobileLabel?: string;
  /** Renders the gold PRO chip after the label. */
  pro?: boolean;
  /** Appears in the desktop nav row. */
  inNav?: boolean;
  /**
   * Leaves this app entirely — rendered as a plain <a> so the browser really
   * navigates. A react-router <Link> would try to resolve it inside this SPA
   * and land on the 404 route, because the destination is served by a
   * different deployment proxied in under the same domain.
   */
  hard?: boolean;
}

export const NAV_LINKS: RbNavLink[] = [
  // "Home" and not "Markets": `/` is now the product gate, and /markets — the
  // USDA reports site — sits directly beside it in this row. Two adjacent
  // entries where one is named for the other's path is how a visitor ends up
  // on the wrong page.
  { key: "markets",     label: "Home",           path: () => "/",             inNav: true },
  // The live DVAuction market page — what `/` used to be. First after Home
  // because the live tape is the reason most cattlemen open the site.
  { key: "dvauction",   label: "DVAuction Live Sales", path: () => "/dvauction-live", inNav: true, mobileLabel: "DVAuction Live" },
  // Every USDA cattle and feed report, server-rendered for search. A separate
  // deployment proxied in at /markets — hence `hard`.
  //
  // inNav:false since 2026-08-09 — NOT hidden. It was promoted to the gold CTA
  // button in the nav's button cluster, and leaving it in the link row too
  // would put the same destination on screen twice. It stays in this array so
  // the mobile hamburger, which maps the whole list, still carries it; the
  // gold button is hidden on phones.
  { key: "usda-reports", label: "USDA Reports",  path: () => "/markets",      hard: true },
  // NOTE: there is deliberately no "Sales" entry. /sales is still live and is
  // reached from the DVAuction Live Sales page and the footer — owner call
  // 2026-08-09. It sat next to "DVAuction Live Sales" here and the two read as
  // the same destination. Re-adding it needs that collision solved first.
  { key: "boxed-beef",  label: "Boxed Beef",     path: rbBoxedBeefPath,       inNav: true },
  { key: "cof",         label: "Cattle on Feed", path: rbCofPath,             inNav: true },
  { key: "fed-cattle",  label: "Fed Cattle",     path: rbFedCattlePath,       inNav: true },
  // /consultant sits behind the authenticated Layout, so a click lands on
  // /auth. That is the intended funnel — the homepage already says "sign up
  // only for the Cattle Consultant".
  { key: "consultant",  label: "Consultant",     path: () => "/consultant",   inNav: true },
  { key: "platform",    label: "Platform",       path: () => "/platform",     inNav: true, pro: true },
  { key: "reports",     label: "Reports",        path: () => "/sale-reports", inNav: true, mobileLabel: "Sale Reports" },
  // Not in the desktop row (it is reachable from the hero and the board), but
  // it belongs in the mobile panel and the footer.
  { key: "leaderboard", label: "Leaderboard",    path: () => "/leaderboard" },
];
